import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon, CalendarIcon, ZapIcon, TrendingUpIcon, BarChart3Icon, UsersIcon, MessageSquareIcon, PhoneIcon, GlobeIcon, CheckCircleIcon, SearchIcon } from 'lucide-react';
import { ScrollReveal } from '../../components/ScrollReveal';
import { SEOHead } from '../../components/SEOHead';
const faqs = [{
  question: 'How did Viridia help TFP Landscaping get more leads?',
  answer: "We implemented Viridia's all-in-one system, which includes GEO-tuned Yelp Ads and an AI-powered Request a Quote (RAQ) system. By responding instantly to inquiries instead of waiting 4+ hours, TFP was able to capture leads before competitors could."
}, {
  question: 'What results did TFP Landscaping see in 30 days?',
  answer: 'In just 30 days, TFP Landscaping saw a 284% increase in leads, totaling 242 Yelp leads. They achieved a 39.95% close rate on those leads, with 28 estimates booked end-to-end entirely by the AI system.'
}];
export function TfpLandscapingPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-1883" data-mp="1snnn7hqq9097">
      <SEOHead title="TFP Landscaping Case Study | 284% More Leads | Viridia Analytics" description="See how TFP Landscaping worked with Viridia Analytics to get 242 Yelp leads, 28 AI-booked estimates, and a 39.95% close rate in 30 days." canonical="/case-studies/tfp-landscaping" keywords="landscaping marketing case study, Yelp ads for landscapers, AI lead conversion, TFP Landscaping" faq={faqs} data-id="element-1884" data-mp="1sp8jpcwlqt9p" />

      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1885" data-mp="2qbpdw9a1gqr8">
        <ScrollReveal data-id="element-1886" data-mp="2q8tf6iqd9y1q">
          <div className="mb-6" data-id="element-1887" data-mp="2lbjici0nek3v">
            <Link to="/case-studies" className="inline-flex items-center text-gray-400 hover:text-white transition-colors text-sm font-medium" data-id="element-1888" data-mp="2lde3nxfu0e8h">
              <ArrowRightIcon className="w-4 h-4 mr-2 rotate-180" data-id="element-1889" data-mp="qz3gdtevxs4t" />
              Back to Case Studies
            </Link>
          </div>

          <div className="bg-viridia-surface border border-gray-800 rounded-2xl overflow-hidden shadow-2xl" data-id="element-1890" data-mp="1goa6yve1hv0b">
            {/* Header / Snapshot */}
            <div className="bg-gray-900 p-8 lg:p-12 relative overflow-hidden border-b border-gray-800" data-id="element-1891" data-mp="1pmm65lsz214d">
              <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px]" data-id="element-1892" data-mp="1um8h1olbv5a0"></div>
              <div className="relative z-10" data-id="element-1893" data-mp="1zk070d2ewhbk">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold tracking-wide uppercase mb-6 border border-blue-500/20" data-id="element-1894" data-mp="1zj8ni0m3aa1n">
                  Landscaping Success Story
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-8 leading-tight max-w-4xl" data-id="element-1895" data-mp="2dbetwuzfx2nw">
                  TFP Landscaping: 284% More Leads and Instant Responses With
                  Viridia's All-in-One System
                </h1>

                {/* Snapshot Pills */}
                <div className="flex flex-wrap gap-3 mb-10" data-id="element-1896" data-mp="bnkbuuwcmkrf">
                  <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-viridia-black border border-gray-700 text-gray-300 text-sm" data-id="element-1897" data-mp="6o7fnjjl81c6">
                    <MapPinIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-1898" data-mp="1q60h9pa8m5z" />
                    Phoenix Landscaper (18 yrs in business)
                  </div>
                  <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-viridia-black border border-gray-700 text-gray-300 text-sm" data-id="element-1899" data-mp="udsaazp3sn1a">
                    <ZapIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-1900" data-mp="pg09fg6hdqky" />
                    All-in-One System
                  </div>
                  <div className="inline-flex items-center px-3 py-1.5 rounded-lg bg-viridia-black border border-gray-700 text-gray-300 text-sm" data-id="element-1901" data-mp="klvyymy0b0f4">
                    <CalendarIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-1902" data-mp="1d4jgxthphzbz" />
                    Client since July 2025
                  </div>
                </div>

                {/* Hero Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6" data-id="element-1903" data-mp="sva53qodnoe0">
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-1904" data-mp="xv6050f3k5hf">
                    <p className="text-3xl sm:text-4xl font-bold text-blue-500 mb-2" data-id="element-1905" data-mp="ixm0m90yz2in">
                      284%
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-1906" data-mp="nx8emkomgmsi">
                      Lead Increase
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-1907" data-mp="1gl4hk74sbaxq">
                    <p className="text-3xl sm:text-4xl font-bold text-blue-500 mb-2" data-id="element-1908" data-mp="11w823p0eevn3">
                      242
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-1909" data-mp="16ujeo7i2vk90">
                      Total Leads
                    </p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-1910" data-mp="1uistgpgvj7qn">
                    <p className="text-3xl sm:text-4xl font-bold text-blue-500 mb-2" data-id="element-1911" data-mp="1zdow0jjqldi0">
                      39.95%
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-1912" data-mp="3f3g1zgagiaff">Close Rate</p>
                  </div>
                  <div className="bg-viridia-black/50 p-6 rounded-xl border border-gray-800" data-id="element-1913" data-mp="3tsc6j3db15ba">
                    <p className="text-3xl sm:text-4xl font-bold text-blue-500 mb-2" data-id="element-1914" data-mp="3os7qfegb6cll">
                      28
                    </p>
                    <p className="text-white font-medium text-sm" data-id="element-1915" data-mp="313yc0xlfcr4h">
                      AI-Booked Estimates
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Challenge / Solution */}
            <div className="p-8 lg:p-12 bg-viridia-black border-b border-gray-800" data-id="element-1916" data-mp="362hu2mwwdirk">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12" data-id="element-1917" data-mp="pdm5vb10tpvg">
                <div data-id="element-1918" data-mp="pfp6sq3pp3ey">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-6" data-id="element-1919" data-mp="lc0xo2zflzrl">
                    <TrendingUpIcon className="w-6 h-6 text-red-500" data-id="element-1920" data-mp="3e6rb2mxbq9qt" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1921" data-mp="3j5c2pxyifl3n">
                    The Challenge
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1922" data-mp="3o5qd3mnl374u">
                    TFP Landscaping is a Phoenix-based operation with 18 years
                    in business. With one owner running multiple crews and no
                    office staff, their average response time was 4+ hours. Yelp
                    ad spend was being wasted because leads went cold before
                    anyone could reply.
                  </p>
                </div>
                <div data-id="element-1923" data-mp="397eevbkkbqbk">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-1924" data-mp="397y134h8x0ve">
                    <BarChart3Icon className="w-6 h-6 text-blue-500" data-id="element-1925" data-mp="2cqbdsc7wk36k" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1926" data-mp="2gqbszz2tsieo">
                    What Viridia Implemented
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1927" data-mp="296t4xeeq5yz8">
                    We deployed the full Viridia system - Yelp Ads
                    management with GEO tuning, an AI-powered Request a Quote
                    (RAQ) system for instant response, and automated lead
                    handling so every inquiry gets answered in seconds, even
                    outside business hours.
                  </p>
                </div>
              </div>
            </div>

            {/* 30-Day Performance Breakdown */}
            <div className="p-8 lg:p-12 bg-viridia-surface border-b border-gray-800" data-id="element-1928" data-mp="1gix1lqunh8th">
              <h3 className="text-2xl font-bold text-white mb-8" data-id="element-1929" data-mp="1ggkihad5oseu">
                30-Day Metrics
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-1930" data-mp="17hz5rvud87lz">
                <div className="space-y-6" data-id="element-1931" data-mp="17kbj1vp6htsp">
                  <div className="bg-viridia-black p-6 rounded-xl border border-gray-800" data-id="element-1932" data-mp="12ivys5utrrbd">
                    <div className="flex justify-between items-center mb-2" data-id="element-1933" data-mp="2qfbz2uj16xw1">
                      <span className="text-gray-400" data-id="element-1934" data-mp="2veyacyfau427">Impressions</span>
                      <span className="text-white font-bold" data-id="element-1935" data-mp="2ve5bskqhukv5">36.4k</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-1936" data-mp="2hevybstoa4uv">
                      <div className="bg-blue-500 h-2 rounded-full" style={{
                      width: '70%'
                    }} data-id="element-1937" data-mp="2mcxdw3rw3m1l"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-1938" data-mp="1yonz3lt3fyjy">
                      25.5k (70%) from Yelp Ads
                    </p>
                  </div>
                  <div className="bg-viridia-black p-6 rounded-xl border border-gray-800" data-id="element-1939" data-mp="22r2hy5wriyb8">
                    <div className="flex justify-between items-center mb-2" data-id="element-1940" data-mp="1orjcxlz90335">
                      <span className="text-gray-400" data-id="element-1941" data-mp="1tq4dxnmbqqsn">Page Visits</span>
                      <span className="text-white font-bold" data-id="element-1942" data-mp="1a3szn7wlipbc">376</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-1943" data-mp="1a5m5ikzdx5ia">
                      <div className="bg-blue-500 h-2 rounded-full" style={{
                      width: '77%'
                    }} data-id="element-1944" data-mp="156j4rcqftlg4"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-1945" data-mp="1t2580rfonv5v">
                      288 (77%) from Yelp Ads
                    </p>
                  </div>
                  <div className="bg-viridia-black p-6 rounded-xl border border-gray-800" data-id="element-1946" data-mp="1o3akjc6rtiff">
                    <div className="flex justify-between items-center mb-2" data-id="element-1947" data-mp="22roue72lu75h">
                      <span className="text-gray-400" data-id="element-1948" data-mp="1xuonjtxxl0db">Total Leads</span>
                      <span className="text-white font-bold" data-id="element-1949" data-mp="2bsmz6rkvaip4">242</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2 mb-2" data-id="element-1950" data-mp="27s33ztjutp5m">
                      <div className="bg-blue-500 h-2 rounded-full" style={{
                      width: '74%'
                    }} data-id="element-1951" data-mp="3s7jfp27brl3a"></div>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-1952" data-mp="3iirq30pqlcvj">
                      179 (74%) from Yelp Ads
                    </p>
                  </div>
                </div>

                <div data-id="element-1953" data-mp="f1jegyllr999">
                  <h4 className="text-lg font-semibold text-white mb-4" data-id="element-1954" data-mp="ezyjj7vdloav">
                    Lead Breakdown
                  </h4>
                  <div className="bg-viridia-black rounded-xl border border-gray-800 overflow-hidden" data-id="element-1955" data-mp="a2q1r8e53ma5">
                    <table className="w-full text-left text-sm" data-id="element-1956" data-mp="54wuec13om8a">
                      <thead className="bg-gray-900 border-b border-gray-800" data-id="element-1957" data-mp="10gowbvefgtl">
                        <tr data-id="element-1958" data-mp="2ptb4on0jaw8t">
                          <th className="p-4 text-gray-400 font-medium" data-id="element-1959" data-mp="2pum8gmtzc3zx">
                            Lead Type
                          </th>
                          <th className="p-4 text-gray-400 font-medium" data-id="element-1960" data-mp="119f36tjqjzpj">
                            Count
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800" data-id="element-1961" data-mp="1aa3gfm1b1w1d">
                        <tr data-id="element-1962" data-mp="1a8scnm7v0oa9">
                          <td className="p-4 text-white flex items-center" data-id="element-1963" data-mp="1f0tk8uu31qty">
                            <MessageSquareIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-1964" data-mp="1f24rj4mqlgpt" />{' '}
                            Messages
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-1965" data-mp="1oxy9j3j862ez">123</td>
                        </tr>
                        <tr data-id="element-1966" data-mp="1t1nxbqs2sfyy">
                          <td className="p-4 text-white flex items-center" data-id="element-1967" data-mp="1t1e29os6bime">
                            <PhoneIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-1968" data-mp="1xyebu9o8f3i9" />{' '}
                            Calls
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-1969" data-mp="3logrom4no9a">53</td>
                        </tr>
                        <tr data-id="element-1970" data-mp="16wkh9yqhr0dp">
                          <td className="p-4 text-white flex items-center" data-id="element-1971" data-mp="16vskwlp2kymd">
                            <GlobeIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-1972" data-mp="11xr11yjtkv9u" />{' '}
                            Website Visits
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-1973" data-mp="1fyjyos5f46h6">41</td>
                        </tr>
                        <tr data-id="element-1974" data-mp="1awuk52izlaoc">
                          <td className="p-4 text-white flex items-center" data-id="element-1975" data-mp="1pvg58sbraij3">
                            <CheckCircleIcon className="w-4 h-4 mr-2 text-blue-500" data-id="element-1976" data-mp="1ptbtjo7m8f89" />{' '}
                            Other Leads
                          </td>
                          <td className="p-4 text-white font-bold" data-id="element-1977" data-mp="1ymoyjoiyf7ap">25</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Bookings & Close Rate */}
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-1978" data-mp="23nmcfowxlf6e">
              <div className="p-8 lg:p-12 bg-viridia-black border-r border-gray-800" data-id="element-1979" data-mp="23l05nrhv73p8">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-1980" data-mp="2n6jule4la3op">
                  <ZapIcon className="w-6 h-6 text-blue-500" data-id="element-1981" data-mp="2s5d9naziy2qz" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1982" data-mp="2dcjiutesy070">
                  AI-Booked Estimates
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6" data-id="element-1983" data-mp="24i52somg6lmu">
                  28 estimates were booked end-to-end by AI directly inside Yelp
                  RAQ. By replacing a 4+ hour lag with an instant response, the
                  AI captured homeowners while they were still actively looking
                  to hire.
                </p>
                <div className="inline-flex items-center px-4 py-2 rounded-lg bg-viridia-surface border border-gray-800 text-white font-medium" data-id="element-1984" data-mp="1zjk2yqb3y3yx">
                  Response Time:{' '}
                  <span className="text-blue-500 ml-2" data-id="element-1985" data-mp="1pjtbkxhug532">
                    From 4+ hours to Instant
                  </span>
                </div>
              </div>
              <div className="p-8 lg:p-12 bg-viridia-black" data-id="element-1986" data-mp="1div1nftyc4r1">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-1987" data-mp="1djd10lbwuz36">
                  <UsersIcon className="w-6 h-6 text-blue-500" data-id="element-1988" data-mp="18ps5r11pcj7o" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1989" data-mp="13si7cvwc4x7p">
                  Close Rate
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6" data-id="element-1990" data-mp="zotym9vyvvkv">
                  Because leads were engaged instantly and qualified
                  automatically, TFP Landscaping reported a 39.95% close rate on
                  the estimates that were booked.
                </p>
                <div className="inline-flex items-center px-4 py-2 rounded-lg bg-viridia-surface border border-gray-800 text-white font-medium" data-id="element-1991" data-mp="kw0icm8vfbf5">
                  Client-Reported Close Rate:{' '}
                  <span className="text-blue-500 ml-2" data-id="element-1992" data-mp="2f5udjdfkhrx1">39.95%</span>
                </div>
              </div>
            </div>

            {/* Owning the Local Map & 12-Month Impact */}
            <div className="grid grid-cols-1 lg:grid-cols-2 border-t border-gray-800" data-id="element-1993" data-mp="2rozs398ibo38">
              <div className="p-8 lg:p-12 bg-viridia-surface border-r border-gray-800" data-id="element-1994" data-mp="2wikfy8gdz1op">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-1995" data-mp="31faweww3rzan">
                  <MapPinIcon className="w-6 h-6 text-blue-500" data-id="element-1996" data-mp="24rem29r9e07v" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1997" data-mp="28sq3hrzzbf4z">
                  GEO & Local Visibility
                </h3>
                <ul className="space-y-4 mb-8" data-id="element-1998" data-mp="2dpgl4jrfmisi">
                  <li className="flex items-start" data-id="element-1999" data-mp="2iqe1qrwsn4rw">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-2000" data-mp="2ir5uluz0aoeh" />
                    <span className="text-gray-300" data-id="element-2001" data-mp="3p5omwiz1hq34">
                      TFP earned more page views than similar landscaping
                      businesses.
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2002" data-mp="34ip55xpf4n5q">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-2003" data-mp="2zgrfolqfcw4c" />
                    <span className="text-gray-300" data-id="element-2004" data-mp="3ee0n0ow4iahp">
                      GEO-tuned campaigns targeted specific high-value
                      neighborhoods.
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2005" data-mp="2lr659zqoxp7x">
                    <CheckCircleIcon className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-2006" data-mp="2grb9pivz0bgj" />
                    <span className="text-gray-300" data-id="element-2007" data-mp="2vg7khnpew2ku">
                      Successfully competing with high-volume brands like Moon
                      Valley Nurseries.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="p-8 lg:p-12 bg-viridia-surface" data-id="element-2008" data-mp="22y26y12j2o6z">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6" data-id="element-2009" data-mp="1y1lif5rf27td">
                  <SearchIcon className="w-6 h-6 text-blue-500" data-id="element-2010" data-mp="dhq3jk7vess8" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4" data-id="element-2011" data-mp="i9rcaw5ty1di">
                  12-Month Impact
                </h3>
                <p className="text-gray-300 mb-6" data-id="element-2012" data-mp="3lwnhquy0h5p">
                  Over the last 12 months, Yelp Ads have generated an additional{' '}
                  <strong className="text-white" data-id="element-2013" data-mp="8hbtxxqmfas3">582 leads</strong> for TFP
                  Landscaping.
                </p>
                <div data-id="element-2014" data-mp="118vlc4xdfv56">
                  <p className="text-sm text-gray-400 mb-3 uppercase tracking-wider font-semibold" data-id="element-2015" data-mp="117aqee75aa6s">
                    Trending Searches Captured:
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-2016" data-mp="r9wcomclvzi6">
                    <span className="px-3 py-1 bg-viridia-black border border-gray-700 rounded-full text-xs text-gray-300" data-id="element-2017" data-mp="r8cv8sf7riet">
                      Fountain Repair{' '}
                      <span className="text-blue-500 ml-1" data-id="element-2018" data-mp="3c1g7dz9q4n83">+87%</span>
                    </span>
                    <span className="px-3 py-1 bg-viridia-black border border-gray-700 rounded-full text-xs text-gray-300" data-id="element-2019" data-mp="7jn3sbeq578c">
                      Turf Installation{' '}
                      <span className="text-blue-500 ml-1" data-id="element-2020" data-mp="3un08cixg7606">+50%</span>
                    </span>
                    <span className="px-3 py-1 bg-viridia-black border border-gray-700 rounded-full text-xs text-gray-300" data-id="element-2021" data-mp="3pogm0hexzs7a">
                      Irrigation{' '}
                      <span className="text-blue-500 ml-1" data-id="element-2022" data-mp="q8a7aph95ojt">+50%</span>
                    </span>
                    <span className="px-3 py-1 bg-viridia-black border border-gray-700 rounded-full text-xs text-gray-300" data-id="element-2023" data-mp="m89uf99sxlev">
                      Landscaping{' '}
                      <span className="text-blue-500 ml-1" data-id="element-2024" data-mp="cigiu89nt6am">+40%</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial */}
            <div className="p-8 lg:p-12 bg-viridia-black border-t border-gray-800 text-center relative" data-id="element-2025" data-mp="155b06wb6jpjv">
              <div className="text-blue-500 text-6xl absolute top-4 left-1/2 -translate-x-1/2 opacity-10 font-serif leading-none" data-id="element-2026" data-mp="1cisdpcvzqaw1">
                "
              </div>
              <p className="text-xl md:text-2xl text-white italic relative z-10 max-w-3xl mx-auto mb-6 leading-relaxed" data-id="element-2027" data-mp="1mf55u6bw0goo">
                I'm getting more jobs with the same ad spend. The auto response
                system is booking me almost 10 new estimates per week, and
                almost half of them become clients.
              </p>
              <p className="text-blue-500 font-bold uppercase tracking-wider text-sm" data-id="element-2028" data-mp="20dldbzhkqb7v">
                Owner, TFP Landscaping - Phoenix, AZ
              </p>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.2} data-id="element-2029" data-mp="2a7bwcouxltg6">
          <div className="mt-12 text-center" data-id="element-2030" data-mp="2e7cn98z7vx44">
            <h3 className="text-2xl font-bold text-white mb-6" data-id="element-2031" data-mp="2e8nrf9wkr6vr">
              Want to see what's leaking leads in your pipeline?
            </h3>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-viridia-black bg-white rounded-lg hover:bg-gray-200 transition-all duration-300" data-id="element-2032" data-mp="3k2t1mspix5nw">
              Book a Strategy Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2033" data-mp="3t5ke8vu4y7h4" />
            </Link>
          </div>
        </ScrollReveal>
      </section>
    </div>;
}